import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError } from '../api/client';
import { ownerApi } from '../api/owner';
import { QuotaBanner } from '../components/QuotaBanner';
import { SuspendedBanner } from '../components/SuspendedBanner';
import type { OwnerBusiness, OwnerProduct, QuotaStatus } from '../types';

interface DraftItem {
  name: string;
  price: string;
  description: string;
}

const EMPTY_DRAFT: DraftItem = { name: '', price: '', description: '' };

export function ProductsManager() {
  const [business, setBusiness] = useState<OwnerBusiness | null>(null);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [products, setProducts] = useState<OwnerProduct[]>([]);
  const [drafts, setDrafts] = useState<DraftItem[]>([{ ...EMPTY_DRAFT }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isMenuMode = business?.site_type === 'menu';
  const isStatic = business?.site_type === 'static';

  const reload = async () => {
    const [b, q, p] = await Promise.all([ownerApi.getBusiness(), ownerApi.getQuota(), ownerApi.listProducts()]);
    setBusiness(b);
    setQuota(q);
    setProducts(p.data);
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const updateDraft = (idx: number, key: keyof DraftItem, value: string) => {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, [key]: value } : d)));
  };

  const addDraftRow = () => setDrafts((prev) => [...prev, { ...EMPTY_DRAFT }]);
  const removeDraftRow = (idx: number) => setDrafts((prev) => prev.filter((_, i) => i !== idx));

  const handleBulkSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const items = drafts
      .filter((d) => d.name.trim())
      .map((d) => ({
        name: d.name.trim(),
        description: d.description.trim() || null,
        price: d.price ? Number(d.price) : null,
      }));
    if (!items.length) return;

    setSaving(true);
    setMessage(null);
    try {
      await ownerApi.bulkCreateProducts(items);
      setDrafts([{ ...EMPTY_DRAFT }]);
      await reload();
      setMessage({ type: 'success', text: `${items.length} item(s) added (1 quota point used).` });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setMessage({ type: 'error', text: 'Monthly edit limit reached.' });
      } else {
        setMessage({ type: 'error', text: 'Could not add items.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await ownerApi.deleteProduct(id);
      await reload();
    } catch {
      setMessage({ type: 'error', text: 'Could not delete item.' });
    }
  };

  const handleToggleAvailable = async (product: OwnerProduct) => {
    try {
      await ownerApi.updateProduct(product.id, { is_available: !product.is_available });
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setMessage({ type: 'error', text: 'Monthly edit limit reached.' });
      }
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  if (isStatic) {
    return (
      <div className="rounded-brand border border-slate-200 bg-white p-6 text-sm text-slate-600">
        Aapke business type (Static Site) me products/menu section available nahi hai.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">{isMenuMode ? 'Menu Items' : 'Products / Catalog'}</h1>

      {business?.is_suspended && <SuspendedBanner />}
      {quota && <QuotaBanner quota={quota} />}

      <div className="rounded-brand border border-slate-200 bg-white">
        <ul className="divide-y divide-slate-100">
          {products.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{p.name}</p>
                {p.description && <p className="truncate text-sm text-slate-500">{p.description}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-sm font-semibold text-brand-primary">
                  {p.price != null ? `₹${p.price}` : '—'}
                </span>
                <button
                  onClick={() => handleToggleAvailable(p)}
                  disabled={business?.is_suspended}
                  className={`rounded-brand px-2 py-1 text-xs font-medium ${
                    p.is_available ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {p.is_available ? 'Available' : 'Hidden'}
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  disabled={business?.is_suspended}
                  className="text-xs font-medium text-red-500 hover:text-red-700"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
          {!products.length && <li className="p-4 text-sm text-slate-500">Koi items nahi hai abhi.</li>}
        </ul>
      </div>

      <form onSubmit={handleBulkSubmit} className="space-y-3 rounded-brand border border-slate-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-slate-700">
          Naye items add karo (bulk add = sirf 1 quota point)
        </h2>
        {drafts.map((draft, idx) => (
          <div key={idx} className="flex flex-wrap items-start gap-2">
            <input
              placeholder="Name"
              value={draft.name}
              onChange={(e) => updateDraft(idx, 'name', e.target.value)}
              className="min-w-[140px] flex-1 rounded-brand border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Price (₹)"
              type="number"
              value={draft.price}
              onChange={(e) => updateDraft(idx, 'price', e.target.value)}
              className="w-28 rounded-brand border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Description (optional)"
              value={draft.description}
              onChange={(e) => updateDraft(idx, 'description', e.target.value)}
              className="min-w-[160px] flex-1 rounded-brand border border-slate-300 px-3 py-2 text-sm"
            />
            {drafts.length > 1 && (
              <button
                type="button"
                onClick={() => removeDraftRow(idx)}
                className="rounded-brand border border-slate-200 px-2 py-2 text-xs text-slate-500 hover:bg-slate-50"
              >
                Remove
              </button>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={addDraftRow}
          className="text-sm font-medium text-brand-secondary hover:underline"
        >
          + Add another row
        </button>

        {message && (
          <p className={`text-sm ${message.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
            {message.text}
          </p>
        )}

        <div>
          <button
            type="submit"
            disabled={saving || business?.is_suspended || (quota ? quota.remaining <= 0 : false)}
            className="rounded-brand bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save items'}
          </button>
        </div>
      </form>
    </div>
  );
}
